import { type FC, useState, useCallback, useEffect } from 'react';
import { usePaginationFragment, graphql } from 'react-relay';

import { FormattedMessage } from 'dibs-react-intl';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import LazyImage from 'dibs-react-lazy-image';
import dibsCss from 'dibs-css';
import classnames from 'classnames';

import { Review } from './Review/Review';

import { type SellerReviewsPhotosCarousel_viewer$key } from './__generated__/SellerReviewsPhotosCarousel_viewer.graphql';
import styles from './SellerReviewsPhotosCarousel.scss';

const HORIZONTAL_OFFSET_IMG = 300;

const viewerFragment = graphql`
    fragment SellerReviewsPhotosCarousel_viewer on Viewer
    @refetchable(queryName: "SellerReviewsPhotosCarouselRefetchQuery")
    @argumentDefinitions(
        first: { type: Int, defaultValue: 2 }
        after: { type: String, defaultValue: "" }
    ) {
        #Variables must match reviewsWithPhotos field variables in SellerReviewsModalHeader_viewer fragment
        orderFeedbackMultiple(
            sellerId: $sellerId
            excludeInAppealProcess: true
            orderByNewest: true
            excludePrivateItemReviews: true
            hasVisibleFeedbackImages: true
            first: $first
            after: $after
        ) @connection(key: "SellerReviewsPhotosCarousel_orderFeedbackMultiple") {
            totalResults
            edges {
                node {
                    ...Review_review
                    serviceId
                    orderPhotos {
                        largePath
                    }
                }
            }
        }
    }
`;

export const SellerReviewsPhotosCarousel: FC<{
    viewer: SellerReviewsPhotosCarousel_viewer$key | null | undefined;
    sellerId: string;
    noTabs?: boolean;
}> = ({ ...props }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const {
        data: viewer,
        loadNext,
        hasNext,
        isLoadingNext,
    } = usePaginationFragment(viewerFragment, props.viewer);

    const totalReviews = viewer?.orderFeedbackMultiple?.totalResults;
    const reviews = viewer?.orderFeedbackMultiple?.edges || [];

    //useEffect handles spam clicks as well
    useEffect(() => {
        /**
         * TODO: Don't load undecesery content in case of spam click. Currently we can't do that because usePaginationFragment
         * doesn't have a good support for windowed pagination with jump to page functionality.
         */
        //Check if we have data for the current index. If no data available load new data up until currentIndex + 1
        if (currentIndex > reviews.length - 2 && hasNext && !isLoadingNext) {
            //will always load one review in advance
            loadNext(currentIndex - (reviews.length - 2));
        }
    }, [loadNext, currentIndex, isLoadingNext, reviews.length, hasNext]);

    const onPageChange = useCallback(
        (index: $TSFixMe) => {
            setCurrentIndex(index);
        },
        [setCurrentIndex]
    );

    const reviewsEl = (
        <div className={classnames(styles.reviewWrapper, { [dibsCss.mtLarge]: props.noTabs })}>
            <Review
                review={reviews[currentIndex]?.node || null}
                showOrderPhoto={false}
                noTabs={props.noTabs}
                hideDetailsSeparator={props.noTabs}
            />
        </div>
    );

    return (
        <>
            {props.noTabs && (
                <div
                    className={classnames(
                        dibsCss.sassyFontHeaderLarge,
                        dibsCss.textCenter,
                        dibsCss.mbLarge
                    )}
                >
                    <FormattedMessage
                        id="SellerReviewsPhotosCarousel.title"
                        defaultMessage="Reviews with Photos"
                    />
                </div>
            )}
            {!props.noTabs && reviewsEl}
            {typeof totalReviews === 'number' ? (
                <Swiper
                    showArrows
                    onPageChange={onPageChange}
                    classNames={{ nextArrow: styles.nextArrow, prevArrow: styles.prevArrow }}
                >
                    {Array(totalReviews)
                        .fill(null)
                        .map((_, index) => {
                            const review = reviews[index]?.node || null;
                            const reviewPhotoUrl = review?.orderPhotos?.[0]?.largePath || '';

                            return (
                                <div key={index} className={styles.itemWrapper}>
                                    {index === 0 ? (
                                        <img className={styles.image} src={reviewPhotoUrl} alt="" />
                                    ) : (
                                        <LazyImage
                                            offsetHorizontal={HORIZONTAL_OFFSET_IMG}
                                            placeholderClass={styles.lazyImagePlaceholder}
                                            className={styles.lazyImage}
                                        >
                                            <img
                                                className={styles.image}
                                                src={reviewPhotoUrl}
                                                alt=""
                                            />
                                        </LazyImage>
                                    )}
                                </div>
                            );
                        })}
                </Swiper>
            ) : (
                <div className={styles.loadingWrapper}>
                    <div className={styles.itemWrapper}>
                        <LazyImage
                            placeholderClass={styles.lazyImagePlaceholder}
                            className={styles.lazyImage}
                        >
                            <div className={styles.image} />
                        </LazyImage>
                    </div>
                </div>
            )}

            {!props.noTabs && (
                <div className={styles.pagination}>
                    {totalReviews && (
                        <FormattedMessage
                            id="SellerReviewsPhotosCarousel.pagination"
                            defaultMessage={`{currentPage, number} of {totalPages, number} {totalPages, plural,
                            one {photo}
                            other {photos}
                        }`}
                            values={{ currentPage: currentIndex + 1, totalPages: totalReviews }}
                        />
                    )}
                </div>
            )}

            {props.noTabs && reviewsEl}
        </>
    );
};
